import React, { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import CompressionConfirmModal from '../ImageCompressionModal/CompressionConfirmModal';
import {
  uploadImageWithOptionalCompression,
  DEFAULT_COMPRESS_THRESHOLD_BYTES,
} from '../../utils/imageUpload';
import {
  dispatchSheetImageUploadUi,
  insertImageFromDataUrl,
  loadNaturalSizeFromUrl,
  SHEET_IMAGE_FILE_SELECTED,
} from '../../utils/spreadsheetImageHelpers';

/**
 * Listens for sheet image file picks (Insert menu / toolbar), runs Cloudinary upload
 * with optional CompressionConfirmModal (original UI), then inserts the returned URL.
 * Modal stays open with buttons disabled until upload finishes, then closes.
 * Toolbar insert-image busy state is only used when there is no compress modal
 * (small images); otherwise the modal’s own loading buttons are enough.
 *
 * @param {() => void} [onAfterImageInserted] — e.g. flush active sheet into React so `/api/update` runs (sheet `change` is debounced 600ms).
 */
export default function SpreadsheetImageUploadBridge({
  spreadsheetInstanceRef,
  isReadOnly,
  onAfterImageInserted,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFileSize, setModalFileSize] = useState(0);
  const [modalButtonsDisabled, setModalButtonsDisabled] = useState(false);
  /** Which action is running after the user confirms (drives button spinners). */
  const [modalUploadingChoice, setModalUploadingChoice] = useState(null);
  const compressPendingRef = useRef(null);

  const resetModal = useCallback(() => {
    setModalOpen(false);
    setModalFileSize(0);
    setModalButtonsDisabled(false);
    setModalUploadingChoice(null);
    compressPendingRef.current = null;
  }, []);

  const finishCompressChoice = useCallback((compress) => {
    const p = compressPendingRef.current;
    if (!p) return;
    compressPendingRef.current = null;
    setModalUploadingChoice(compress ? 'compress' : 'original');
    setModalButtonsDisabled(true);
    p.resolve(compress);
  }, []);

  const onModalClose = useCallback(() => {
    const p = compressPendingRef.current;
    if (p) {
      compressPendingRef.current = null;
      p.reject(new Error('cancelled'));
    }
    resetModal();
  }, [resetModal]);

  const runUpload = useCallback(
    async (file) => {
      if (!file || !file.type?.startsWith?.('image/')) return;
      if (isReadOnly) return;
      if (!spreadsheetInstanceRef?.current) return;

      const useCompressModal = file.size > DEFAULT_COMPRESS_THRESHOLD_BYTES;
      const busyMessage = 'Compressing and uploading image...';

      try {
        const url = await uploadImageWithOptionalCompression(file, {
          thresholdBytes: DEFAULT_COMPRESS_THRESHOLD_BYTES,
          promptCompress: useCompressModal
            ? () =>
                new Promise((resolve, reject) => {
                  compressPendingRef.current = { resolve, reject, file };
                  setModalButtonsDisabled(false);
                  setModalFileSize(file.size);
                  setModalOpen(true);
                })
            : undefined,
          // Avoid toolbar “compressing/uploading” overlay while the modal is open;
          // uploadImageWithOptionalCompression calls onBusyChange after the user confirms.
          onBusyChange: useCompressModal
            ? undefined
            : () => {
                dispatchSheetImageUploadUi({ active: true, message: busyMessage });
              },
        });

        const { width, height } = await loadNaturalSizeFromUrl(url);
        insertImageFromDataUrl(spreadsheetInstanceRef, url, width, height);
        onAfterImageInserted?.();

        window.setTimeout(() => {
          message.success('Image uploaded successfully!');
        }, 0);
        if (useCompressModal) {
          await new Promise((r) => setTimeout(r, 400));
        }
      } catch (err) {
        const msg = String(err?.message || '').toLowerCase();
        const cancelled = msg.includes('cancel');
        if (!cancelled) {
          window.setTimeout(() => {
            message.error(err?.message || 'Image upload failed');
          }, 0);
        }
      } finally {
        dispatchSheetImageUploadUi({ active: false });
        resetModal();
      }
    },
    [isReadOnly, onAfterImageInserted, resetModal, spreadsheetInstanceRef]
  );

  useEffect(() => {
    const onSelect = (ev) => {
      const f = ev.detail?.file;
      if (f) runUpload(f);
    };
    window.addEventListener(SHEET_IMAGE_FILE_SELECTED, onSelect);
    return () => window.removeEventListener(SHEET_IMAGE_FILE_SELECTED, onSelect);
  }, [runUpload]);

  return (
    <CompressionConfirmModal
      open={modalOpen}
      fileSize={modalFileSize}
      uploadingChoice={modalUploadingChoice}
      choiceButtonsDisabled={modalButtonsDisabled}
      onCompress={() => finishCompressChoice(true)}
      onUploadOriginal={() => finishCompressChoice(false)}
      onCancel={onModalClose}
      zIndex={5000}
    />
  );
}
